import React from 'react';
import { Avatar } from '@mui/material';
import Chip from 'Components/Chip';
import BasicTable from 'Components/BasicTable';
import { colors } from 'Constants/Colors';
import type { AdminCourseI } from 'types';
import { ContainerTable, PeriodNote, TitleCell } from './CoursesTable.styled';

// Fila plana que consume BasicTable: las 5 primeras posiciones son las columnas
// visibles (limit=5), la 6a es la foto que pinta la celda del creador y la
// ultima es el curso tipado para resolver el click.
const TITLE_COLUMN = 0;
const CATEGORY_COLUMN = 3;
const CREATOR_COLUMN = 4;
const CREATOR_PHOTO_INDEX = 5;
const COURSE_INDEX = 6;

/**
 * Texto de la ventana vigente para el listado administrativo.
 *
 * Aquí se muestra la ventana que corre hoy. "Sin ciclo" significa que el
 * curso no tiene un periodo abierto en este momento; "(sin plazo)" significa
 * que sí hay ciclo abierto, pero no fecha límite dentro de él.
 */
const getCoursePeriodLabel = (course: AdminCourseI): string => {
  if (!course.currentPeriod) return 'Sin ciclo';
  if (!course.currentPeriod.dueAt) return `Ventana del ${course.currentPeriod.startsAt} (sin plazo)`;
  return `Ventana del ${course.currentPeriod.startsAt} al ${course.currentPeriod.dueAt}`;
};

export interface CoursesTablePropsI {
  courses: AdminCourseI[];
  colorCategory: Record<string, string>;
  headers: string[];
  loading: boolean;
  onCourseClick: (course: AdminCourseI) => void;
}

export default function CoursesTable({ courses, colorCategory, headers, loading, onCourseClick }: CoursesTablePropsI) {
  const rows = courses.map(course => [
    course.name,
    course.description,
    course.priority,
    course.category,
    course.creator?.fullName,
    course.creator?.photo,
    course,
  ]);

  const thereIsComponent = (data: unknown[], index: number): React.ReactNode => {
    if (index === TITLE_COLUMN) {
      const course = data[COURSE_INDEX] as AdminCourseI;

      return (
        <TitleCell>
          <span>{data[index] as string}</span>
          <PeriodNote>{getCoursePeriodLabel(course)}</PeriodNote>
        </TitleCell>
      );
    }
    if (index === CATEGORY_COLUMN)
      return (
        <Chip style={{ border: 'none', background: `${colorCategory[data[index] as string]}`, color: colors.WHITE }}>
          {null}
          <h4>{data[index] as string}</h4>
        </Chip>
      );
    if (index === CREATOR_COLUMN)
      return (
        <div style={{ display: 'flex' }}>
          <Chip>
            <Avatar alt={data[index] as string} src={data[CREATOR_PHOTO_INDEX] as string} />
            <h4>{data[index] as string}</h4>
          </Chip>
        </div>
      );
    return data[index] as React.ReactNode;
  };

  return (
    <ContainerTable>
      <BasicTable
        information={rows}
        headers={headers}
        limit={5}
        thereIsComponent={thereIsComponent}
        widthColumn="25%"
        clickable
        clickAction={(row: unknown[]) => onCourseClick(row[COURSE_INDEX] as AdminCourseI)}
        isLoading={loading}
      />
    </ContainerTable>
  );
}
